import React from 'react';
import { InputAdornment, TextField, Typography } from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon } from '@vedmarket/ui-kit';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '../../../../config/constants';
import './search.scss'

type SearchProps = {
    initialValue?: string;
    onSearch?: (query: string) => void;
    onClear?: () => void;
};

const Search = ({ initialValue = '', onSearch, onClear }: SearchProps) => {

    const [query, setQuery] = React.useState(initialValue)

    const { t } = useTranslation()
    const navigate = useNavigate()

    React.useEffect(() => {
        setQuery(initialValue)
    }, [initialValue])

    const handleSearch = (searchValue = query) => {
        const trimmedQuery = searchValue.trim()

        if (trimmedQuery) {
            if (onSearch) {
                onSearch(trimmedQuery)
                return
            }

            navigate(`${ROUTES.SEARCH}?q=${encodeURIComponent(trimmedQuery)}`)
        }
    }

    const handleClear = () => {
        setQuery('')

        if (onClear) {
            onClear()
            return
        }

        if (onSearch) {
            onSearch('')
            return
        }

        navigate(ROUTES.SEARCH)
    }

    const handleExampleClick = () => {
        const example = 'API'

        setQuery(example)
        handleSearch(example.toLowerCase())
    }

    return (
        <div className='search-container'>
            <TextField
                value={query}
                onChange={({ target: { value } }) => setQuery(value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        handleSearch()
                    }
                }}
                label={t('text__searchThroughTheHelpCenter')}
                variant='outlined'
                fullWidth
                InputLabelProps={{
                    shrink: true,
                }}
                sx={{
                    '.MuiOutlinedInput-root': {
                        minHeight: '48px',
                    },
                    '.MuiOutlinedInput-input': {
                        padding: '13px 14px',
                    },
                    '.MuiInputAdornment-root': {
                        marginLeft: '12px',
                    },
                    svg: {
                        width: '24px',
                        height: '24px',
                    },
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position='end'>
                            {query ? (
                                <ClearIcon
                                    style={{
                                        cursor: 'pointer',
                                        fill: 'currentColor',
                                        width: '24px',
                                        height: '24px',
                                    }}
                                    onClick={handleClear}
                                />
                            ) : (
                                <SearchIcon
                                    style={{
                                        cursor: 'pointer',
                                        fill: 'currentColor',
                                        width: '24px',
                                        height: '24px',
                                    }}
                                    onClick={() => handleSearch()}
                                />
                            )}
                        </InputAdornment>
                    ),
                }}
            />
            <Typography variant='body2' sx={{
                marginTop: 2,
                lineHeight: '22px',
            }}>
                {t('common__text__example')},&nbsp;
                <span className='search-container__example' onClick={handleExampleClick}>
                    API
                </span>
            </Typography>
        </div>
    )
};

export default Search;
